import React, { useLayoutEffect, useRef, useState } from "react";
import {
  HANDLE_MODE_ALL,
  HANDLE_MODE_ASPECT_RATIO,
  HANDLE_MODE_ASPECT_RATIO_W_ROTATION,
} from "@/constants/HandleModeConstants.mjs";
import { Positions } from "../../constants/TransformerConstants";
import { toZoomedSpace } from "@/utils/ZoomUtils";
import { isIOS } from "@/constants/BrowserConstants";
import { ICON_VERSION } from "@showitinc/showit-wc/src/components/Icon/Constants.js";
import { chooseGripSide, getGripStalk } from "@/utils/math/RotateGrip.mjs";

// fingers need a bigger target than a cursor
const HANDLE_RADIUS = isIOS ? "6" : "3";
// screen px of the rotate grip's icon, one of si-icon's sizes
const ROTATE_GRIP_SIZE = isIOS ? 20 : 16;
const ROTATE_GRIP_RADIUS = ROTATE_GRIP_SIZE / 2;
// the sprite si-icon draws from, so the grip follows the icon library
const ROTATE_ICON_HREF = `icons/${ICON_VERSION}/showit-icons.svg#rotate`;
const STROKE_WIDTH = 2;
// screen px around an iPad handle where a finger still takes it
const TOUCH_HIT_RADIUS = 20;
// screen px between an edge and the rotate grip; on iPad far enough that its
// hit area and the edge's middle handle's only touch
const ROTATE_STALK = isIOS ? TOUCH_HIT_RADIUS * 2 : 24;
// screen px the grip keeps from the edges of the view: on iPad, its finger area
const ROTATE_GRIP_CLEARANCE = isIOS ? TOUCH_HIT_RADIUS : ROTATE_GRIP_RADIUS + STROKE_WIDTH;

/** The handle each grip side starts a rotation as; rotation ignores which. */
const GRIP_SIDE_HANDLES = {
  bottom: Positions.BOTTOM_MIDDLE,
  top: Positions.TOP_MIDDLE,
  right: Positions.RIGHT_MIDDLE,
  left: Positions.LEFT_MIDDLE,
};

/**
 * @typedef {Object} HandleSpec
 * @prop {string} key
 * @prop {string=} id
 * @prop {string=} testId
 * @prop {string} className
 * @prop {number} cx - In tool space.
 * @prop {number} cy - In tool space.
 * @prop {number=} r - In tool space; the handle radius when left out.
 * @prop {Function} onPointerDown
 */

/**
 * @param {{ width: number, height: number, resizeStart: (handle: string) => Function }} props
 * @returns {HandleSpec[]}
 */
const cornerHandles = ({ width, height, resizeStart }) => [
  {
    key: "topLeftCorner",
    id: "tl",
    className: "transformer-handle transformer-resize-tl",
    cx: 0,
    cy: 0,
    onPointerDown: resizeStart(Positions.TOP_LEFT),
  },
  {
    key: "topRightCorner",
    id: "tr",
    className: "transformer-handle transformer-resize-tr",
    cx: width,
    cy: 0,
    onPointerDown: resizeStart(Positions.TOP_RIGHT),
  },
  {
    key: "bottomLeftCorner",
    id: "bl",
    className: "transformer-handle transformer-resize-bl",
    cx: 0,
    cy: height,
    onPointerDown: resizeStart(Positions.BOTTOM_LEFT),
  },
  {
    key: "bottomRightCorner",
    id: "br",
    className: "transformer-handle transformer-resize-br",
    cx: width,
    cy: height,
    onPointerDown: resizeStart(Positions.BOTTOM_RIGHT),
  },
];

/**
 * @param {{ width: number, height: number, showAllHandles: boolean, resizeStart: (handle: string) => Function }} props
 * @returns {HandleSpec[]}
 */
const edgeHandles = ({ width, height, showAllHandles, resizeStart }) => [
  ...(showAllHandles
    ? [
        {
          key: "topEdge",
          id: "tm",
          className: "transformer-handle transformer-resize-tm",
          cx: width / 2,
          cy: 0,
          onPointerDown: resizeStart(Positions.TOP_MIDDLE),
        },
        {
          key: "bottomEdge",
          id: "bm",
          className: "transformer-handle transformer-resize-bm",
          cx: width / 2,
          cy: height,
          onPointerDown: resizeStart(Positions.BOTTOM_MIDDLE),
        },
      ]
    : []),
  {
    key: "leftEdge",
    id: "lm",
    className: "transformer-handle transformer-resize-lm",
    cx: 0,
    cy: height / 2,
    onPointerDown: resizeStart(Positions.LEFT_MIDDLE),
  },
  {
    key: "rightEdge",
    id: "rm",
    className: "transformer-handle transformer-resize-rm",
    cx: width,
    cy: height / 2,
    onPointerDown: resizeStart(Positions.RIGHT_MIDDLE),
  },
];

/**
 * Draws handles as visible circles.
 * @param {{ handles: HandleSpec[], handleRadius: number, strokeWidth: number }} props
 * @returns {JSX.Element}
 */
const HandleCircles = ({ handles, handleRadius, strokeWidth }) =>
  handles.map(({ key, id, testId, className, cx, cy, r = handleRadius, onPointerDown }) => (
    <circle
      key={key}
      id={id}
      data-testid={testId}
      className={className}
      cx={cx}
      cy={cy}
      r={r}
      onPointerDown={onPointerDown}
      style={{ strokeWidth }}
    />
  ));

/**
 * Invisible finger-sized hit areas around handles, which start what the
 * handle does. Drawn under every visible handle, so a handle always takes a
 * press on itself over a neighbor's hit area.
 * @param {{ handles: HandleSpec[], hitRadius: number }} props
 * @returns {JSX.Element}
 */
const HandleHitAreas = ({ handles, hitRadius }) =>
  handles.map(({ key, cx, cy, onPointerDown }) => (
    <circle
      key={`${key}-hit`}
      className="transformer-handle-hit"
      cx={cx}
      cy={cy}
      r={hitRadius}
      onPointerDown={onPointerDown}
    />
  ));

/**
 * The rotate grip: the rotate icon on a disc, which takes presses anywhere in
 * the icon's box rather than only on its strokes.
 * @param {{ grip: HandleSpec }} props
 * @returns {JSX.Element}
 */
const RotateGrip = ({ grip: { testId, className, cx, cy, r, onPointerDown } }) => (
  <>
    <circle
      data-testid={testId}
      className={className}
      cx={cx}
      cy={cy}
      r={r}
      onPointerDown={onPointerDown}
    />
    <use
      className="transformer-rotate-icon"
      href={ROTATE_ICON_HREF}
      x={cx - r}
      y={cy - r}
      width={r * 2}
      height={r * 2}
    />
  </>
);

const RotateHandles = ({ width, height, rotateStart, handleRadius, strokeWidth }) => (
  <>
    <circle
      key="br-rotate"
      id="br-rotate"
      className="transformer-rotate-handle transformer-rotate-br"
      cx={width + 8}
      cy={height + 8}
      r={handleRadius}
      onPointerDown={rotateStart(Positions.BOTTOM_RIGHT)}
      style={{ strokeWidth }}
    />
    <circle
      key="bl-rotate"
      id="bl-rotate"
      className="transformer-rotate-handle transformer-rotate-bl"
      cx={-8}
      cy={height + 8}
      r={handleRadius}
      onPointerDown={rotateStart(Positions.BOTTOM_LEFT)}
      style={{ strokeWidth }}
    />
    <circle
      key="tl-rotate"
      id="tl-rotate"
      className="transformer-rotate-handle transformer-rotate-tl"
      cx={-8}
      cy={-8}
      r={handleRadius}
      onPointerDown={rotateStart(Positions.TOP_LEFT)}
      style={{ strokeWidth }}
    />
    <circle
      key="tr-rotate"
      id="tr-rotate"
      className="transformer-rotate-handle transformer-rotate-tr"
      cx={width + 8}
      cy={-8}
      r={handleRadius}
      onPointerDown={rotateStart(Positions.TOP_RIGHT)}
      style={{ strokeWidth }}
    />
  </>
);

/**
 * The side of the tool the rotate grip goes on: below unless the stage view
 * cuts it off there, then the first of top, right, left that it shows on.
 * Placed again on every render and on scroll (not on a view resize alone),
 * but held while a transform runs and placed once when it ends: a rotation
 * never pulls the grip from under the finger, and a move or resize doesn't
 * measure the page every frame.
 * @param {Object} props
 * @param {React.RefObject<SVGElement>} props.anchorRef - Any element drawn in the tool's SVG.
 * @param {React.RefObject<HTMLElement>=} props.scrollerRef - The stage scroller; without it the grip stays below.
 * @param {number[]=} props.matrix - The drawn tool's matrix.
 * @param {boolean} props.transforming
 * @param {number} props.width
 * @param {number} props.height
 * @param {number} props.stalkLength - In tool px.
 * @param {number} props.zoom
 * @returns {import("@/utils/math/RotateGrip.mjs").GripSide}
 */
const useRotateGripSide = ({
  anchorRef,
  scrollerRef,
  matrix,
  transforming,
  width,
  height,
  stalkLength,
  zoom,
}) => {
  const [side, setSide] = useState("bottom");
  // renewed each render, so a scroll long after it still places from the latest tool
  const placeRef = useRef();
  placeRef.current = () => {
    const scroller = scrollerRef?.current;
    const svg = anchorRef.current?.closest("svg");
    if (!scroller || !svg || !matrix) return;
    // the tool's SVG fills the scaled overlay's container: its corner is the
    // tool matrix's origin, and both rects are visual px (zoom is scale())
    const { left, top } = svg.getBoundingClientRect();
    const view = scroller.getBoundingClientRect();
    // client size leaves out the scrollbars
    const viewLeft = view.left + scroller.clientLeft;
    const viewTop = view.top + scroller.clientTop;
    setSide(
      chooseGripSide({
        width,
        height,
        stalkLength,
        matrix,
        zoom,
        clearance: ROTATE_GRIP_CLEARANCE,
        origin: [left, top],
        visibleRect: {
          left: viewLeft,
          top: viewTop,
          right: viewLeft + scroller.clientWidth,
          bottom: viewTop + scroller.clientHeight,
        },
      }),
    );
  };

  // every render outside a transform (the drawn tool's matrix is a fresh
  // array each time anyway), including the one that ends it. Before paint, so
  // a grip that moves never shows a frame on the old side.
  useLayoutEffect(() => {
    if (!transforming) placeRef.current();
  });

  // on the document, catching the scroller's scrolls as they pass: StageView
  // attaches its scroller ref after the handles inside it lay out
  useLayoutEffect(() => {
    if (transforming) return undefined;
    const onScroll = (e) => {
      if (e.target === scrollerRef?.current) placeRef.current();
    };
    document.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => document.removeEventListener("scroll", onScroll, { capture: true });
  }, [transforming, scrollerRef]);

  return side;
};

const Handles = ({
  width,
  height,
  handleMode,
  rotateStart,
  resizeStart,
  multi = false,
  zoom,
  matrix,
  scrollerRef,
  transforming = false,
}) => {
  const showAllHandles = handleMode === HANDLE_MODE_ALL;
  const isAspectRatio =
    handleMode === HANDLE_MODE_ASPECT_RATIO || handleMode === HANDLE_MODE_ASPECT_RATIO_W_ROTATION;
  const canRotate = !isAspectRatio || handleMode === HANDLE_MODE_ASPECT_RATIO_W_ROTATION;
  const hasCorners = showAllHandles || isAspectRatio;
  const handleRadius = toZoomedSpace(HANDLE_RADIUS, zoom);
  const strokeWidth = toZoomedSpace(STROKE_WIDTH, zoom);
  const corners = hasCorners ? cornerHandles({ width, height, resizeStart }) : [];
  const edges =
    !multi && !isAspectRatio ? edgeHandles({ width, height, showAllHandles, resizeStart }) : [];

  // A grip on a stalk out from the middle of an edge rotates. Rotation pivots
  // on the tool center whichever handle starts it.
  const stalkRef = useRef();
  const stalkLength = toZoomedSpace(ROTATE_STALK, zoom);
  const gripSide = useRotateGripSide({
    anchorRef: stalkRef,
    scrollerRef,
    matrix,
    transforming,
    width,
    height,
    stalkLength,
    zoom,
  });
  const gripRadius = toZoomedSpace(ROTATE_GRIP_RADIUS, zoom);
  const stalk = getGripStalk(gripSide, width, height, stalkLength);
  // the stalk stops at the icon's edge rather than running into its gap
  const stalkEnd = getGripStalk(gripSide, width, height, stalkLength - gripRadius).grip;
  const grip = canRotate
    ? [
        {
          key: "rotate-grip",
          testId: "transformer-rotate-grip",
          className: `transformer-rotate-grip transformer-rotate-grip-${gripSide}`,
          cx: stalk.grip[0],
          cy: stalk.grip[1],
          r: gripRadius,
          onPointerDown: rotateStart(GRIP_SIDE_HANDLES[gripSide]),
        },
      ]
    : [];
  const hitRadius = toZoomedSpace(TOUCH_HIT_RADIUS, zoom);
  // a quarter of the shorter side at most: neighboring handles' areas only
  // touch, and the middle of the selection is left to move it
  const resizeHitRadius = Math.min(hitRadius, Math.min(width, height) / 4);

  return (
    <>
      {isIOS && (
        <>
          <HandleHitAreas handles={[...corners, ...edges]} hitRadius={resizeHitRadius} />
          <HandleHitAreas handles={grip} hitRadius={hitRadius} />
        </>
      )}
      <HandleCircles handles={corners} {...{ handleRadius, strokeWidth }} />
      {/* iPad leaves out the invisible corner zones: touch has no hover to
          reveal them, and they would catch fingers meant for the corners */}
      {canRotate && !isIOS && (
        <RotateHandles {...{ width, height, rotateStart, handleRadius, strokeWidth }} />
      )}
      {canRotate && (
        <line
          ref={stalkRef}
          className="transformer-rotate-stalk"
          x1={stalk.base[0]}
          y1={stalk.base[1]}
          x2={stalkEnd[0]}
          y2={stalkEnd[1]}
          style={{ strokeWidth }}
        />
      )}
      <HandleCircles handles={edges} {...{ handleRadius, strokeWidth }} />
      {grip.map((spec) => (
        <RotateGrip key={spec.key} grip={spec} />
      ))}
    </>
  );
};

export default Handles;
